import { useEffect, useMemo, useRef } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { getProcurementQueue } from '../../shared/api/procurement.api'
import type { ClientCategory, RequestItemPriority } from '../../shared/api/types'
import type { DashboardQueueFilters } from './useDashboardQueueLink'

/** Серверные фильтры очереди из тулбара (часть состояния useQueueFilters). */
export interface QueueServerFilters {
  assignedToMe: boolean
  overdue: boolean
  burning: boolean
  stale: boolean
  needsInfo: boolean
  rework: boolean
  category: ClientCategory | ''
  priority: RequestItemPriority | ''
}

/**
 * Загрузка очереди квот: бесконечная подгрузка страниц по курсору + sentinel
 * для автодогрузки. Вынесено из ProcurementQueuePage.tsx (14.09.2026, правило
 * ≤500 строк) без изменения ключа кэша и параметров запроса. Флаги берутся с
 * первой страницы — на всех страницах они одинаковы.
 */
export function useQueueQuery(
  filters: QueueServerFilters,
  dashFilters: DashboardQueueFilters,
  showHidden: boolean,
) {
  const { assignedToMe, overdue, burning, stale, needsInfo, rework, category, priority } = filters
  const { data, isLoading, isError, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: [
        'procurement',
        'queue',
        { assignedToMe, overdue, burning, stale, needsInfo, rework, category, priority },
        dashFilters,
        showHidden,
      ],
      initialPageParam: undefined as string | undefined,
      queryFn: ({ pageParam }) =>
        getProcurementQueue({
          assignedToMe: assignedToMe || undefined,
          overdue: overdue || undefined,
          burning: burning || undefined,
          stale: stale || undefined,
          needsInfo: needsInfo || undefined,
          rework: rework || undefined,
          clientCategory: category || undefined,
          priority: priority || undefined,
          procState: dashFilters.procState ?? undefined,
          new: dashFilters.onlyNew || undefined,
          zeroPrice: dashFilters.zeroPrice || undefined,
          wentToInvoice: dashFilters.wentToInvoice || undefined,
          noOffer: dashFilters.noOffer || undefined,
          inWork: dashFilters.inWork || undefined,
          noAnswer: dashFilters.noAnswer || undefined,
          activeOnly: dashFilters.activeOnly || undefined,
          buyerId: dashFilters.buyerId ?? undefined,
          requestType: dashFilters.requestType ?? undefined,
          showHidden: showHidden || undefined,
          cursor: pageParam,
          limit: 1000,
        }),
      getNextPageParam: (last) => (last.hasMore ? (last.nextCursor ?? undefined) : undefined),
    })

  const items = useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data])

  // Sentinel для IntersectionObserver — триггер догрузки следующей страницы.
  const sentinelRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          void fetchNextPage()
        }
      },
      { rootMargin: '200px' },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const firstPage = data?.pages[0]
  return {
    items,
    isLoading,
    isError,
    error,
    isFetchingNextPage,
    sentinelRef,
    // Скрытие позиций (только ГД).
    canHide: firstPage?.canHide ?? false,
    hiddenCount: firstPage?.hiddenCount ?? 0,
    // Потолок выборки, если в него упёрлись (№319); null — очередь полная.
    truncatedLimit: firstPage?.truncated ? (firstPage.limit ?? null) : null,
  }
}
